<template>
    <div class="input-wrap" :class="{error : errors.length > 0 || collection.validation.error , processing : processing}">
        <label :for="collection.name">{{label}}<span v-if="collection.validation.required">*</span></label>
        <div class="drop-target-wrapper">
            <div class="drop-target" :class="{highlighted: highlighted}" @dragover="dragEnter" @dragenter="dragEnter" @dragleave="dragLeave" @drop="handleDrop">
                <div class="content">
                    <div class="upload-icon">
                        <SvgUpload :fill="fill"/>
                        <Spinner />
                    </div>
                    <p class="prompt">{{dropText}} <a id="fileSelect" href="#" @click.prevent="browseForFile">select</a>
                    </p>
                    <div v-if="maxFiles > 1 && collection.value.length > 0" class="file-listing">
                        <div v-for="(file, index) in collection.value" :key="file.name" class="file">
                            <span class="remove" @click="removeFromCollection(index)" title="Remove file"><SvgCross /></span>
                            <span class="filename">{{ file.fields.name.value }}</span>
                        </div>
                    </div>
                    
                    <p v-if="collection.value.length > 0 && (!maxFiles || maxFiles === 1)" class="filename">{{collection.value[0].fields.name.value}}</p>
                    
                    <p v-if="collection.value.length === 0" class="filename">No file selected</p>

                </div>
            </div>
            <input :id="collection.name" type="file" ref="input" @change="selectFile"/>
        </div>
        <div class="error">{{collection.validation.error}}</div>
        <div class="error" :class="{'multiple-files' : maxFiles > 1 && errors.length > 1}" v-for="error in errors" :key="error">{{error}}</div>
    </div>
</template>

<script>
    import SvgUpload from "../../icons/SvgUpload";
    import SvgCross from "../../icons/SvgCross";
    import Spinner from "../../icons/Spinner";

    import FileModel from "../../../../models/FileModel";

    export default {
        name: 'InputFileUpload',
        components: {SvgUpload, SvgCross, Spinner},
        props: [
            'collection',
            'label',
            'allowedFileTypes',
            'maxFileSize',
            'maxFiles'
        ],
        data() {
            return {
                highlighted: false,
                errors: [],
                processing: false
            };
        },
        computed: {
            fill() {
                if (this.highlighted){
                    return 'active';
                } else if (this.errors.length > 0) {
                    return 'error';
                } else {
                    return '#7A7A7A';
                }
            },
            dropText() {
                if (this.maxFiles === 1) {
                    return 'Drop a file here or';
                } else {
                    return 'Drop files here or select';
                }
            },
            formattedMaxFileSize() {
                if (this.maxFileSize === 0) return '0 Bytes';

                const k = 1024;
                const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
                const i = Math.floor(Math.log(this.maxFileSize) / Math.log(k));
                return parseFloat((this.maxFileSize / Math.pow(k, i)).toFixed(0)) + ' ' + sizes[i];
            }
  
        },
        watch: {
            collection(val) {
                if(val.value.length === 0){
                    this.errors = [];
                }
            }
        },
        methods: {
            dragEnter(e) {
                e.preventDefault();
                e.stopPropagation();
                this.highlighted = true;
            },
            dragLeave(e) {
                e.preventDefault();
                e.stopPropagation();
                this.highlighted = false;
            },
            handleDrop(e) {
                e.preventDefault();
                e.stopPropagation();
                this.highlighted = false;
                this.fileSelected(e.dataTransfer.files);
                this.$emit('fileSelected')
            },
            browseForFile(e) {
                this.$refs.input.click();
            },
            selectFile(e) {
                this.fileSelected(e.target.files);
                this.$emit('fileSelected')
                
            },
            removeFromCollection(index) {
                this.collection.value.splice(index, 1);
            },
            async fileSelected(files) {
                
                if (files && files.length && !this.processing) {
                    this.processing = true;
                    this.errors = [];
                    let uploadCount = this.maxFiles || 1;
                    if (uploadCount > 1) {
                        uploadCount = uploadCount - this.collection.value.length;
                    } else {
                        this.collection.value = [];
                    }
                    
                    if (uploadCount > 0) {
                        if (files.length > uploadCount) {
                            if (this.maxFiles === 1) {
                                this.errors.push(`File upload limit reached, some files have not been added. Only 1 file can be uploaded at a time.`);
                            } else {
                                this.errors.push(`File upload limit reached, some files have not been added. Only ${this.maxFiles} files can be uploaded at a time.`);
                            }
                        }
                        for (let i = 0; i < files.length && i < uploadCount; i++) {
                            const file = files[i];
                            const name = file.name;
                            const ext = name.substr(name.lastIndexOf('.') + 1);
                            const size = file.size;
                            const content = file.content;
                            let errorList = [];

                            if (this.maxFileSize && size > this.maxFileSize) {
                                errorList.push(`File too large (max ${this.formattedMaxFileSize}).`);
                            }
                            if (!ext || this.allowedFileTypes && !this.allowedFileTypes.includes(ext)) {
                                errorList.push('Invalid file type (Accepted types - ' + this.allowedFileTypes.join(', ') + ').');
                            }

                            if (errorList.length === 0){

                                await new Promise((resolve, reject) => {
                                    const reader = new FileReader();

                                    reader.onload = () => {
                                        
                                        let binary = "";
                                        const bytes = new Uint8Array(reader.result);
                                        const length = bytes.byteLength;
                                        for (let j = 0; j < length; j++) {
                                            binary += String.fromCharCode(bytes[j]);
                                        }

                                        const fileContent = btoa(binary);

                                        if (!fileContent) {
                                            reject('No file content.');
                                        }
                                        resolve(fileContent);
                                    };

                                    reader.onerror = (error) => {
                                        reject(error);
                                    };

                                    reader.readAsArrayBuffer(file);
                                })
                                .then((fileContent) => {
                                        this.collection.value.push(
                                            new FileModel({
                                                name: name,
                                                ext: ext,
                                                size: size,
                                                content: fileContent
                                            })
                                        );
                                })
                                .catch((error) => {
                                    errorList.push(error);
                                });

                            }
                                if (errorList.length > 0){
                                    this.errors.push((files.length > 1 ? name + ' : ' : '') + errorList.join(' '));
                                }

                        }
                        this.collection.validate();
                    } else {
                        // Max file upload limit reached
                        this.errors.push(`File upload limit reached. Only ${this.maxFiles} files can be uploaded at a time.`);
                    }

                    this.processing = false;
                }

            }
        }
    };
</script>

<style scoped>

    .drop-target-wrapper {border-radius: 1.5rem; background: #dadada; padding: .5rem; margin: .5rem 0;}
        .drop-target-wrapper input {display: none;}

    .drop-target {border: .25rem dashed #808080; border-radius: 1.15rem; padding: 1rem; text-align: center;}
        .drop-target .content {padding: 1.5rem;}
            .drop-target .content .upload-icon {width: 6rem; height: 6rem; display: inline-block; margin-bottom: .5rem; position: relative;}
                .drop-target .content .upload-icon .svg {width: 100%; max-width: 6rem; /*opacity: .5;*/ position: absolute; top: 0; right: 0; bottom: 0; left: 0; transform: scale(1); transition: .25s;}
                .drop-target .content .upload-icon >>> .spinner {width: 6rem; height: 6rem; font-size: .75rem; position: absolute; top: 0; right: 0; bottom: 0; left: 0; transform: scale(0); transition: .25s;}
                .processing .drop-target .content .upload-icon .svg {transform: scale(0);}
                .processing .drop-target .content .upload-icon >>> .spinner {transform: scale(1);}
            .drop-target .content .prompt {opacity: .75;}
            .drop-target .content .filename {font-family: 'Roboto Condensed', sans-serif; font-weight: bold;}
            .drop-target .content .file-listing {margin: 1rem -.25rem 0 -.25rem;}
                .drop-target .content .file-listing .file {display: inline-block; margin: 0 .25rem; max-width: 100%; position: relative; background-color: #C3D9EE; font-family: 'Roboto Condensed', sans-serif; font-size: .9rem; box-sizing: border-box; padding: .2rem .4rem .2rem 1.5rem; border: 1px solid #808080;}
                .drop-target .content .file-listing .file + .file {margin-top: .5rem;}
                    .drop-target .content .file-listing .file > .filename {user-select: none;}
                    .drop-target .content .file-listing .file > .remove {width: 1rem; height: 1rem; position: absolute; margin: auto; left: 0.2rem; top: 0; bottom: 0; cursor: pointer;}
                    .drop-target .content .file-listing .file > .remove:hover {transform: scale(1.25);}
                    .drop-target .content .file-listing .file > span {overflow-wrap: break-word;}

    .error .drop-target {border: .25rem dashed var(--colour-error);}
    .error .drop-target .content .filename {color: var(--colour-error);}
    .error.multiple-files {text-align: left;}

    .drop-target.highlighted {border: .25rem dashed var(--colour-active);}
    .drop-target.highlighted .content .filename {color: var(--colour-active);}

</style>